import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { PatientControllerApi } from '../api/generated';
import type { PatientResponse } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectRoomProperties } from '../store/roomProperties/roomPropertiesSlice';
import type { RoomProperty } from '../store/roomProperties/roomPropertiesSlice';
import { showError } from '../store/errorDialog/errorDialogSlice';
import type {
  FormState,
  PatientCreatePrefill
} from '../components/PatientDetails/types/patientDetailsCard.types';
import { emptyForm, formFromPatient } from '../components/PatientDetails/types/patientDetailsCard.types';
import { extractFieldErrors } from '../utils/fieldErrorUtils';

interface UsePatientFormOptions {
  patient?: PatientResponse | null;
  locationId: string;
  mode: 'view' | 'edit' | 'create';
  createPrefill?: PatientCreatePrefill;
  onEditStart: () => void;
  onEditEnd: () => void;
  onPatientUpdated?: () => void;
  onPatientCreated?: (created: PatientResponse) => void | Promise<void>;
}

export const usePatientForm = ({
  patient,
  locationId,
  mode,
  createPrefill,
  onEditStart,
  onEditEnd,
  onPatientUpdated,
  onPatientCreated
}: UsePatientFormOptions) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const allRoomProperties = useAppSelector(selectRoomProperties);

  const patientRoomNeeds = useMemo(
    () =>
      patient
        ? patient.patientRoomNeedIds
            .map((id) => allRoomProperties.find((rp) => rp.id === id))
            .filter((rp): rp is RoomProperty => rp !== undefined)
        : [],
    [patient, allRoomProperties]
  );

  const [form, setForm] = useState<FormState>(() => {
    if ((mode === 'edit' || mode === 'create') && patient) {
      return formFromPatient(patient, patientRoomNeeds);
    }
    if (mode === 'create') {
      return { ...emptyForm, ...createPrefill };
    }
    return emptyForm;
  });

  // Refs, not state: nothing renders from them, and the sync effect has to read
  // them without listing them as dependencies.
  const isDirtyRef = useRef(false);
  const lastSeededPatientIdRef = useRef<string | null>(patient?.id ?? null);

  // Marks the form dirty so a background refresh cannot clobber it. Internal
  // seeding deliberately goes through the raw setter instead.
  const setFormAndMarkDirty = useCallback<Dispatch<SetStateAction<FormState>>>((value) => {
    isDirtyRef.current = true;
    setForm(value);
  }, []);

  // `patient` and `patientRoomNeeds` are re-minted by refreshes that have
  // nothing to do with this form (any setPatients dispatch, any roomProperties
  // store write). Re-seeding on those wipes an edit in progress, so a dirty
  // form skips the re-seed wholesale — every field is protected identically.
  useEffect(() => {
    const patientId = patient?.id ?? null;

    if (patient) {
      // A different patient always wins: the form now belongs to someone else.
      if (patientId !== lastSeededPatientIdRef.current) {
        lastSeededPatientIdRef.current = patientId;
        isDirtyRef.current = false;
        setForm(formFromPatient(patient, patientRoomNeeds));
        return;
      }
      if (isDirtyRef.current) return;
      setForm(formFromPatient(patient, patientRoomNeeds));
    } else if (mode === 'create') {
      if (isDirtyRef.current) return;
      lastSeededPatientIdRef.current = null;
      setForm({ ...emptyForm, ...createPrefill });
    }
  }, [patient, patientRoomNeeds, mode, createPrefill]);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const showErrorDialog = useCallback(
    (message: string) => {
      dispatch(showError({ message }));
    },
    [dispatch]
  );

  const handleEditClick = useCallback(() => {
    if (patient) {
      setForm(formFromPatient(patient, patientRoomNeeds));
    }
    // Entering edit mode is an explicit sync point with the server state.
    isDirtyRef.current = false;
    setFieldErrors({});
    onEditStart();
  }, [patient, patientRoomNeeds, onEditStart]);

  const patientRequest = useMemo(
    () => ({
      name: form.name,
      cprNumber: form.cpr,
      email: form.email || undefined,
      phoneNumber: form.phone || undefined,
      note: form.comment || undefined,
      patientRoomNeedIds: form.roomNeeds.map((rp) => rp.id)
    }),
    [form]
  );

  const handleCreatePatient = useCallback(async () => {
    setFieldErrors({});

    let response;
    try {
      const config = createApiConfiguration();
      const patientApi = new PatientControllerApi(config);
      response = await patientApi.createPatient({ locationId, patientRequest });
    } catch (error) {
      console.error('Failed to create patient:', error);
      const fe = extractFieldErrors(error);
      if (Object.keys(fe).length > 0) {
        setFieldErrors(fe);
      } else {
        showErrorDialog(t('error-patient-create-failed'));
      }
      return;
    }

    // Saved: the post-create refresh landing on this form is wanted, not a
    // clobber. A failed create deliberately leaves the form dirty.
    isDirtyRef.current = false;

    // The create already succeeded — isolate callback failures from the
    // create error path so they can't be reported as create failures.
    if (onPatientCreated) {
      try {
        await onPatientCreated(response.data);
      } catch (postCreateError) {
        console.error('onPatientCreated callback failed:', postCreateError);
      }
    }
    onEditEnd();
    onPatientUpdated?.();
  }, [
    locationId,
    patientRequest,
    onEditEnd,
    onPatientUpdated,
    onPatientCreated,
    showErrorDialog,
    t
  ]);

  const handleUpdatePatient = useCallback(async () => {
    if (!patient?.id) return;

    setFieldErrors({});

    try {
      const config = createApiConfiguration();
      const patientApi = new PatientControllerApi(config);
      await patientApi.updatePatient({
        locationId,
        patientId: patient.id,
        patientRequest
      });

      // Saved: the refreshToken-driven refetch that follows should re-seed.
      isDirtyRef.current = false;
      onEditEnd();
      onPatientUpdated?.();
    } catch (error) {
      console.error('Failed to update patient:', error);
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        showErrorDialog(
          typeof error.response.data === 'string'
            ? error.response.data
            : t('error-patient-update-failed')
        );
      } else {
        const fe = extractFieldErrors(error);
        if (Object.keys(fe).length > 0) {
          setFieldErrors(fe);
        } else {
          showErrorDialog(t('error-patient-update-failed'));
        }
      }
    }
  }, [
    patient?.id,
    locationId,
    patientRequest,
    onEditEnd,
    onPatientUpdated,
    showErrorDialog,
    t
  ]);

  return {
    form,
    setForm: setFormAndMarkDirty,
    fieldErrors,
    patientRoomNeeds,
    allRoomProperties,
    handleEditClick,
    handleCreatePatient,
    handleUpdatePatient
  };
};
